import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { Times } from '@styled-icons/fa-solid/Times';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { device } from '../../constants/device-sizes';
import { API_URL } from '../../constants/string-constants';
import ModalButton from './ModalButton';
import TitleText from './TitleText';

const CloseButton = styled(Times)`
  cursor: pointer;
  position: absolute;
  width: 2.5rem;
  height: 2.5rem;
  top: 1rem;
  right: 1rem;
  margin: 1rem 1rem;
  font-size: 2rem;
  transition: all 0.2s linear;
  color: var(--clr-sec);
  :hover {
    color: red;
  }
`;

const ModalWrapper = styled.div`
  width: 100%;
  padding: 1rem;
  @media ${device.tablet} {
    width: 50%;
    position: auto;
    top: auto;
  }
  top: 0px;
  position: absolute;
`;

const ModalContent = styled.div`
  padding: 16px;
  background-color: var(--clr-bg);
  width: 100%;
  border-radius: 12px;
  shadow-opacity: 0.18;
  shadow-radius: 4px;
  shadow-color: #000;
  shadow-offset: 0px 4px;
  elevation: 10;
`;

const Description = styled.p`
  color: var(--clr-sec);
  font-size: 16px;
  text-align: center;
  margin: 1rem 0;
`;

const InputContainer = styled.div`
  margin: 1rem 0;
`;

const Label = styled.label`
  display: block;
  color: var(--clr-sec);
  font-size: 14px;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const AccountNumberInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 16px;
  border: 2px solid var(--clr-prim);
  border-radius: 8px;
  background-color: var(--clr-bg);
  color: var(--clr-sec);
  text-align: center;
  letter-spacing: 1px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--clr-tert);
  }

  &::placeholder {
    color: var(--clr-sec);
    opacity: 0.5;
    letter-spacing: 2px;
  }
`;

const ErrorText = styled.p`
  color: #ff6b6b;
  font-size: 14px;
  text-align: center;
  margin: 0.5rem 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const LoadingWarning = styled.p`
  color: #ff9800;
  font-size: 13px;
  text-align: center;
  margin: 0.75rem 0;
  font-style: italic;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0%, 100% {
      opacity: 0.8;
    }
    50% {
      opacity: 1;
    }
  }
`;

const ModalButtonSC = styled.div`
  cursor: pointer;
  flex-grow: 1;
  margin: 0 0.5rem;
`;

const AccountSignInModal = ({ visible, onClose, onSuccess, onSocketReconnect }) => {
  const [accountNumber, setAccountNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSignIn = async () => {
    if (!accountNumber.trim()) {
      setError('Please enter your account number');
      return;
    }

    if (accountNumber.length !== 20) {
      setError('Account number must be 20 digits');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const auth = getAuth();

      // Sign in with account number (for returning users on new devices)
      const verifyResponse = await fetch(API_URL + 'account/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_number: accountNumber.trim(),
        }),
      });

      if (verifyResponse.ok) {
        const verifyData = await verifyResponse.json();
        
        // Sign in with the custom token to switch to the existing account
        await signInWithCustomToken(auth, verifyData.custom_token);
        console.log('Signed in to existing account:', verifyData.user_id);
        
        // Get new token and reconnect socket with the switched user
        const newToken = await auth.currentUser.getIdToken();
        if (onSocketReconnect) {
          onSocketReconnect(newToken, auth.currentUser);
        }
        
        const analytics = getAnalytics();
        logEvent(analytics, 'account_number_signin', {
          has_subscription: verifyData.has_subscription,
        });

        // Sign-in successful
        onSuccess(accountNumber.trim());
      } else {
        const errorData = await verifyResponse.json();
        setError(errorData.error || 'Failed to sign in with account number');
      }
    } catch (error) {
      console.error('Error signing in with account number:', error);
      setError('Failed to sign in with account number');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 20) {
      setAccountNumber(value);
      setError('');
    }
  };

  const isValidAccountNumber = accountNumber.length === 20;

  return (
    <Modal
      isOpen={visible}
      onRequestClose={onClose}
      style={{
        overlay: { zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <CloseButton onClick={onClose} />

          <TitleText>Sign In with Account Number</TitleText>

          <Description>
            Enter your existing 20-digit account number to sign in.
          </Description>

          <InputContainer>
            <Label htmlFor='account-number-input'>Account Number</Label>
            <AccountNumberInput
              id='account-number-input'
              type='text'
              value={accountNumber}
              onChange={handleInputChange}
              placeholder='12345678901234567890'
              maxLength={20}
              autoFocus
            />
          </InputContainer>

          {error && <ErrorText>{error}</ErrorText>}
          
          {loading && (
            <LoadingWarning>
              Sign-in can take 10+ seconds. Please don't close this menu.
            </LoadingWarning>
          )}

          <ButtonContainer>
            <ModalButtonSC
              width='45%'
              as={ModalButton}
              isAffirmative={false}
              onClick={onClose}
            >
              Cancel
            </ModalButtonSC>
            <ModalButtonSC
              width='45%'
              as={ModalButton}
              isAffirmative={true}
              enabled={isValidAccountNumber && !loading}
              isLoading={loading}
              onClick={handleSignIn}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </ModalButtonSC>
          </ButtonContainer>
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default AccountSignInModal;
